// Super Admin: the god image shown in the middle of each dashboard banner, per temple (PNG, saved immediately).
import React, { useState } from "react";
import api, { fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Landmark } from "lucide-react";
import { dashImageUrl, refreshDashTemples } from "./TempleDash";

const KINDS = [
  ["PROTOCOL", "Protocol Dashboard"],
  ["SAREES", "Sarees Dashboard"],
  ["ANNA", "Annaprasadam Dashboard"],
  ["HUNDIAL", "Hundial Dashboard"],
  ["IDENTITY", "Identity & Passes Dashboard"],
  ["IDCARD", "ID Card Background (4 x 6 in)"],
];
const CARD = "IDCARD"; // full-card artwork for the Duty Pass / Seva Card / Children Tag (PNG or JPG, up to 6 MB)

export default function DashImagesEditor({ temple }) {
  const [versions, setVersions] = useState(temple.dash_image_v || {});
  const [busy, setBusy] = useState("");

  const save = async (kind, image) => {
    setBusy(kind);
    try {
      const r = await api.put(`/temples/${temple.temple_id}/dash-image/${kind}`, { image });
      setVersions((v) => { const x = { ...v }; if (r.data.version) x[kind] = r.data.version; else delete x[kind]; return x; });
      refreshDashTemples();
      toast.success(kind === CARD ? (image ? "ID card background saved" : "ID card background removed") : (image ? "Dashboard image saved" : "Dashboard image removed"));
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(""); }
  };

  const pick = (kind, file) => {
    if (!file) return;
    const card = kind === CARD;
    if (!["image/png", "image/webp", ...(card ? ["image/jpeg"] : [])].includes(file.type)) { toast.error(card ? "Please choose a PNG or JPG image" : "Please choose a PNG image (transparent background looks best)"); return; }
    if (file.size > (card ? 6 : 3) * 1024 * 1024) { toast.error(`Image too large (max ${card ? 6 : 3} MB)`); return; }
    const reader = new FileReader();
    reader.onload = () => save(kind, reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="col-span-2 rounded-lg border border-dashed border-[#E8551E] bg-[#FFF7F0] p-3" data-testid="temple-dash-images">
      <p className="text-xs font-bold uppercase tracking-wider text-[#B0121F]">Dashboard God Images · shown in the middle of each dashboard banner</p>
      <p className="text-[11px] text-slate-500 mb-2">PNG with a transparent background, tall (portrait) works best, e.g. 600 x 800 px · max 3 MB · saved immediately.
        <b className="text-[#B0121F]"> ID Card Background:</b> the full 4 x 6 in card artwork (2 : 3, e.g. 1200 x 1800 px, PNG / JPG, max 6 MB) — the Duty Pass, Seva Card and Children Tag print on it; remove it to go back to the standard card.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {KINDS.map(([kind, label]) => {
          const v = versions[kind];
          return (
            <div key={kind} className="rounded-lg bg-gradient-to-b from-[#C81E1E] to-[#E8541E] p-2 text-center" data-testid={`dash-img-${kind.toLowerCase()}`}>
              <div className="h-28 flex items-center justify-center">
                {v ? <img src={dashImageUrl(temple.temple_id, kind, v)} alt={label} className="max-h-28 max-w-full object-contain drop-shadow" />
                  : <Landmark className="w-10 h-10 text-white/60" />}
              </div>
              <p className="text-[11px] font-semibold text-white mt-1 leading-tight">{label}</p>
              <div className="flex justify-center gap-2 mt-1.5">
                <label className={`text-[11px] px-2 py-0.5 rounded bg-white text-[#B0121F] font-semibold cursor-pointer ${busy === kind ? "opacity-50 pointer-events-none" : ""}`}>
                  {busy === kind ? "Saving…" : v ? "Replace" : "Upload"}
                  <input type="file" accept={kind === CARD ? "image/png,image/webp,image/jpeg" : "image/png,image/webp"} className="hidden" data-testid={`dash-img-input-${kind.toLowerCase()}`}
                    onChange={(e) => { pick(kind, e.target.files?.[0]); e.target.value = ""; }} />
                </label>
                {v && <button type="button" className="text-[11px] px-2 py-0.5 rounded bg-white/20 text-white font-semibold" onClick={() => save(kind, "")}>Remove</button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
